import { EditReview, EditReviewAnalysis, PerformerContract, RolePerformerType } from '@talon/web/lib/api';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Card,
  CardBody,
  CardHeader,
  Carousel,
  CheckBox,
  FormField,
  FormFieldProps,
  Heading,
  Image,
  Select,
  Text,
} from 'grommet';
import { Alert, Checkmark, Close, StatusGood } from 'grommet-icons';
import { PropsWithChildren } from 'react';
import { borderRadiusSize } from 'src/theme';
import { Divider } from '../../Divider';
import { ClassifiersList } from './ClassifiersList';
import { UnknownAnalyses } from './EditReviewModal';
import { ManualClassification } from './ManualClassification';

export type UnknownPerformersPanelProps = {
  unknownAnalyses: UnknownAnalyses;
  editReview?: EditReview;
  contracts: PerformerContract[];
  selectedPerformers: Set<string>;
  processingAction: string | null;
  setEnlargedFrameUrl: (val: string) => void;
  setShowMergeDialog: (show: boolean) => void;
  togglePerformerSelection: (editId: string, performerId: string) => void;
  handleIdentifyPerformer: (editId: string, unknownPerformerId: string, contractedPerformerId: string) => Promise<void>;
  handleReclassify: (editId: string, performerId: string, newType: RolePerformerType, reason?: string) => Promise<void>;
  handleMarkInvalid: (editId: string, performerId: string, reason?: string) => Promise<void>;
};

export const UnknownPerformersPanel = ({
  unknownAnalyses,
  editReview,
  contracts,
  selectedPerformers,
  processingAction = null,
  setEnlargedFrameUrl,
  togglePerformerSelection,
  handleIdentifyPerformer,
  handleReclassify,
  handleMarkInvalid,
}: UnknownPerformersPanelProps) => {
  /** Render **/

  const PerformerInfo = ({ analysis, ...props }: BoxProps & { analysis: EditReviewAnalysis }) => (
    <Box align="center" gap="small" {...props}>
      <Box
        height="small"
        width="small"
        background={{ color: 'light-3' }}
        align="center"
        border={{ size: 'small', color: 'light-4', style: 'dashed' }}
        justify="center"
        round={borderRadiusSize}
      >
        <Text color="text-weak">No Photo</Text>
      </Box>
      <Box gap="xsmall" align="center">
        <Text truncate weight="bold">
          {analysis.performerName || analysis.performerId}
        </Text>
        {analysis.mergedFrom && analysis.mergedFrom.length > 0 && (
          <Text size="xsmall" color="text-weak">
            Merged from: {analysis.mergedFrom.join(', ')}
          </Text>
        )}
        <Text size="small">({analysis.frameAppears.length} frames)</Text>
        {analysis.invalidDetection && (
          <Text size="small" color="status-critical" weight="bold">
            Invalid
          </Text>
        )}
      </Box>
    </Box>
  );

  const PerformerFrames = ({ frameUrls, ...props }: BoxProps & { frameUrls?: string[] }) => (
    <Box height="medium" flex style={{ cursor: 'pointer', position: 'relative' }} gap="xsmall" {...props}>
      <Carousel controls={true} style={{ borderRadius: borderRadiusSize }}>
        {frameUrls?.map((frameUrl) => (
          <Box key={frameUrl} onClick={() => setEnlargedFrameUrl(frameUrl)} style={{ position: 'relative' }}>
            <Image src={frameUrl} />
          </Box>
        ))}
      </Carousel>
      <Box alignSelf="center">
        <Text size="xsmall">🔍 Click image to enlarge</Text>
      </Box>
    </Box>
  );

  const AnalysisCard = ({ children, ...props }: BoxProps & PropsWithChildren) => (
    <Card round="none" elevation="none" flex background="light-3" {...props}>
      {children}
    </Card>
  );

  const AnalysisCardHeader = ({ analysis, ...props }: BoxProps & { analysis: EditReviewAnalysis }) => (
    <CardHeader
      background={{
        color: analysis.type === RolePerformerType.OnCameraPrincipal ? 'status-warning' : 'status-ok',
      }}
      {...props}
    >
      <Text weight="bold">Classification: {analysis.type}</Text>
      {analysis.manuallyClassified && (
        <Box direction="row" gap="xsmall" align="center">
          <StatusGood size="small" color="text" />
          <Text size="small" color="text" weight={500}>
            Manually classified
          </Text>
        </Box>
      )}
    </CardHeader>
  );

  const AnalysisCardBody = ({ children, ...props }: BoxProps & PropsWithChildren) => (
    <CardBody
      border={[
        { side: 'vertical', size: 'small', color: 'light-4' },
        { side: 'bottom', size: 'small', color: 'light-4' },
      ]}
      {...props}
    >
      {children}
    </CardBody>
  );

  const InvalidDetection = ({ analysis, ...props }: BoxProps & { analysis: EditReviewAnalysis }) => (
    <Box background={{ color: 'status-critical' }} pad="small" {...props}>
      <Text weight="bold" color="white">
        Marked as Invalid Detection
      </Text>
      {analysis.invalidReason && (
        <Text size="small" color="white">
          Reason: {analysis.invalidReason}
        </Text>
      )}
      {analysis.markedInvalidAt && (
        <Text size="xsmall" color="white">
          {new Date(analysis.markedInvalidAt).toLocaleString()}
        </Text>
      )}
    </Box>
  );

  const IdentifyAsDropdown = ({ analysis, ...props }: FormFieldProps & { analysis: EditReviewAnalysis }) => (
    <FormField label="Identify As Contracted Performer:" margin="none" {...props}>
      <Select
        size="small"
        placeholder="Select performer..."
        options={contracts.map((c) => ({
          label: `${c.performer.first_name} ${c.performer.last_name} (${c.role.performer_type})`,
          value: c.performer.id,
          contract: c,
        }))}
        labelKey="label"
        valueKey="value"
        onChange={({ option }) =>
          handleIdentifyPerformer(editReview?.edit.id || '', analysis.performerId, option.value)
        }
        disabled={processingAction === analysis.performerId}
      >
        {(option) => (
          <Box direction="row" gap="small" align="center" pad="xsmall">
            {option.contract?.characterPhotos?.[0]?.signedUrl ? (
              <Box width="40px" height="40px" flex={false}>
                <Image src={option.contract.characterPhotos[0].signedUrl} fit="cover" style={{ borderRadius: '4px' }} />
              </Box>
            ) : (
              <Box
                width="40px"
                height="40px"
                background="light-3"
                align="center"
                justify="center"
                style={{ borderRadius: '4px' }}
              >
                <Text size="xsmall" color="text-weak">
                  No Photo
                </Text>
              </Box>
            )}
            <Text>{option.label}</Text>
          </Box>
        )}
      </Select>
    </FormField>
  );

  const IdentifiedAsInfo = ({ analysis, ...props }: BoxProps & { analysis: EditReviewAnalysis }) => (
    <Box background={{ color: 'status-ok' }} {...props}>
      <Text weight="bold" color="white">
        Identified As Contracted Performer
      </Text>
      {analysis.identifiedBy && analysis.identifiedAt && (
        <Text size="xsmall" color="white">
          By: {analysis.identifiedBy} on {new Date(analysis.identifiedAt).toLocaleString()}
        </Text>
      )}
    </Box>
  );

  const UpgradeToPrincipalButton = ({ analysis, ...props }: ButtonProps & { analysis: EditReviewAnalysis }) => (
    <Button
      size="small"
      icon={<Checkmark />}
      label="Upgrade to Principal"
      onClick={() =>
        handleReclassify(
          editReview?.edit.id || '',
          analysis.performerId,
          RolePerformerType.OnCameraPrincipal,
          'Manual upgrade from UI'
        )
      }
      disabled={processingAction === analysis.performerId}
      {...props}
    />
  );

  const DowngradeToExtraButton = ({ analysis, ...props }: ButtonProps & { analysis: EditReviewAnalysis }) => (
    <Button
      size="small"
      icon={<Close />}
      label="Downgrade to Extra"
      onClick={() =>
        handleReclassify(
          editReview?.edit.id || '',
          analysis.performerId,
          RolePerformerType.Extra,
          'Manual downgrade from UI'
        )
      }
      disabled={processingAction === analysis.performerId}
      {...props}
    />
  );

  const MarkInvalidButton = ({ analysis, ...props }: ButtonProps & { analysis: EditReviewAnalysis }) => (
    <Button
      size="small"
      icon={<Alert />}
      label="Mark Invalid"
      onClick={() => handleMarkInvalid(editReview?.edit.id || '', analysis.performerId, 'False positive - not a face')}
      disabled={processingAction === analysis.performerId}
      secondary
      {...props}
    />
  );

  return (
    <Box direction="column" flex="grow">
      {/* Header */}
      <Box gap="small" margin={{ bottom: 'medium' }}>
        <Box gap="xsmall">
          <Heading level={3} margin="none">
            Unknown Performers
          </Heading>
          <Text size="small" color="text-weak">
            These performers were detected but don't match any contracted performers.
          </Text>
        </Box>
        <Divider height="2px" />
      </Box>

      {/* Body */}
      {unknownAnalyses.map(({ analysis, frameUrls }) => (
        <Card
          key={analysis.performerId}
          gap="small"
          border={{
            color: analysis.invalidDetection
              ? 'status-critical'
              : selectedPerformers.has(analysis.performerId)
                ? 'brand'
                : 'light-4',
            size: 'small',
          }}
          pad={{ horizontal: 'medium', bottom: 'medium', top: 'small' }}
          margin={{ bottom: 'medium' }}
          background={{
            color: analysis.invalidDetection
              ? 'light-2'
              : selectedPerformers.has(analysis.performerId)
                ? 'light-3'
                : 'light-1',
          }}
          style={{ opacity: analysis.invalidDetection ? 0.6 : 1 }}
          elevation="none"
          width="100%"
        >
          {/* Selection Checkbox */}
          <CardHeader pad={{ vertical: 'xxsmall' }}>
            {!analysis.invalidDetection && (
              <CheckBox
                checked={selectedPerformers.has(analysis.performerId)}
                onChange={() => togglePerformerSelection(editReview?.edit.id || '', analysis.performerId)}
              />
            )}
          </CardHeader>

          <CardBody direction="row" gap="medium" align="start">
            {/* Photos */}
            <PerformerInfo analysis={analysis} />

            <Box flex gap="small">
              {/* Frames */}
              <PerformerFrames frameUrls={frameUrls} />

              <Box flex gap="medium" margin={{ top: 'xsmall' }}>
                {/* Analysis */}
                <AnalysisCard>
                  {/* Classification */}
                  <AnalysisCardHeader analysis={analysis} pad="small" />

                  <AnalysisCardBody pad="small" gap="xsmall">
                    {/* Invalid Detection Info */}
                    {analysis.invalidDetection && <InvalidDetection analysis={analysis} />}

                    {!analysis.invalidDetection && (
                      <Box gap="small">
                        {/* Manual Classification Info */}
                        {analysis.manuallyClassified && analysis.manualClassificationReason && (
                          <ManualClassification analysis={analysis} />
                        )}

                        {/* Classifiers List */}
                        {analysis.type === RolePerformerType.OnCameraPrincipal &&
                          (analysis.foreground ||
                            analysis.interactsWithProduct ||
                            analysis.reactsToNarration ||
                            analysis.voice) && <ClassifiersList analysis={analysis} />}

                        {/* Identified Performer Info */}
                        {!analysis.identifiedAs && <IdentifyAsDropdown analysis={analysis} />}
                        {analysis.identifiedAs && <IdentifiedAsInfo analysis={analysis} />}
                      </Box>
                    )}
                  </AnalysisCardBody>
                </AnalysisCard>

                {/* Action Buttons */}
                <Box gap="small" direction="row" justify="between">
                  {analysis.type === RolePerformerType.Extra && (
                    <UpgradeToPrincipalButton analysis={analysis} fill="horizontal" />
                  )}
                  {analysis.type === RolePerformerType.OnCameraPrincipal && (
                    <DowngradeToExtraButton analysis={analysis} fill="horizontal" />
                  )}
                  <MarkInvalidButton analysis={analysis} fill="horizontal" />
                </Box>
              </Box>
            </Box>
          </CardBody>
        </Card>
      ))}
    </Box>
  );
};
